// TalentDiscovery Component
// Main page for searching and discovering player profiles

import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaFilter, FaStar, FaMapMarkerAlt, FaBookmark, FaRegBookmark } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import styles from './TalentDiscovery.module.css';
import { useTalentSearch } from '../../hooks/playerProfile/useTalentSearch';
import { useNotification } from '../../context/NotificationContext';
import { useUser } from '../../context/UserContext';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import PlayerCard from './components/PlayerCard';
import SearchFilters from './components/SearchFilters';
import WatchlistModal from './components/WatchlistModal';
import talentDiscoveryService from '../../services/playerProfile/talentDiscoveryService';

const TalentDiscovery = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { user, userProfile, userClubs } = useUser();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [watchlist, setWatchlist] = useState(new Set());
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showWatchlistModal, setShowWatchlistModal] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Check if user has role that allows watchlist features (Coach/Manager/Admin)
  const canUseWatchlist = useMemo(() => {
    if (!Array.isArray(userClubs)) return false;
    
    return userClubs.some((membership) => {
      const role = (membership.role || '').toLowerCase();
      // Backend requires Coach, Manager, or Admin role
      return ['coach', 'manager', 'admin', 'clubmanager', 'clubowner', 'creator', 'owner'].includes(role);
    });
  }, [userClubs]);

  // Custom hook for search functionality
  const {
    profiles,
    loading,
    error,
    pagination,
    filters,
    performSearch,
    updateFilters,
    clearFilters,
    changePage,
    activeFilterCount
  } = useTalentSearch();

  // Check authentication status
  useEffect(() => {
    if (user) {
      setIsAuthReady(true);
    } else {
      setIsAuthReady(false);
    }
  }, [user]);

  // Load watchlist when authenticated (but don't auto-search)
  useEffect(() => {
    if (isAuthReady && user) {
      // Only load watchlist if user has required role
      if (canUseWatchlist) {
        loadWatchlist();
      }
      // Don't perform initial search - wait for user to search
    }
  }, [isAuthReady, user, canUseWatchlist]);

  // Load user's watchlist
  const loadWatchlist = async () => {
    // Don't attempt to load if no user
    if (!user) return;
    
    try {
      const result = await talentDiscoveryService.getWatchlist({ limit: 1000 });
      const watchlistIds = new Set(result.data.map(item => item.profileId));
      setWatchlist(watchlistIds);
    } catch (err) {
      // Silently fail for watchlist - it's not critical
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setHasSearched(true);
    // Backend expects 'q' not 'query'
    updateFilters({ q: searchQuery });
  };

  // Toggle watchlist
  const toggleWatchlist = async (profileId, e) => {
    e.stopPropagation();
    
    // Check if user has permission to use watchlist
    if (!canUseWatchlist) {
      showError('Watchlist features require Coach, Manager, or Admin role in a club');
      return;
    }

    try {
      if (watchlist.has(profileId)) {
        await talentDiscoveryService.removeFromWatchlist(profileId);
        setWatchlist(prev => {
          const newSet = new Set(prev);
          newSet.delete(profileId);
          return newSet;
        });
        showSuccess('Removed from watchlist');
      } else {
        // Show modal for adding to watchlist
        const profile = profiles.find(p => p._id === profileId);
        setSelectedPlayer(profile);
        setShowWatchlistModal(true);
      }
    } catch (err) {
      showError('Failed to update watchlist');
    }
  };

  // Handle add to watchlist
  const handleAddToWatchlist = async (data) => {
    try {
      await talentDiscoveryService.addToWatchlist(selectedPlayer._id, data);
      setWatchlist(prev => new Set([...prev, selectedPlayer._id]));
      showSuccess('Added to watchlist');
      setShowWatchlistModal(false);
      setSelectedPlayer(null);
    } catch (err) {
      showError('Failed to add to watchlist');
    }
  };

  // Navigate to profile in new tab
  const viewProfile = (profile, e) => {
    // The profile object has userId which can be an object or string
    const userId = profile.userId?._id || profile.userId;
    
    if (userId) {
      const profileUrl = `/player-profile/${userId}`;
      
      // Check if user wants to open in new tab (Ctrl/Cmd + click or middle click)
      if (e?.ctrlKey || e?.metaKey || e?.button === 1) {
        window.open(profileUrl, '_blank');
      } else {
        // Default: open in new tab
        window.open(profileUrl, '_blank');
      }
    } else {
      showError('Unable to view profile - user ID not found');
    }
  };

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <div className={styles.container}>
        <div className={styles.authPrompt}>
          <div className={styles.authPromptContent}>
            <h2>Authentication Required</h2>
            <p>Please log in to search for talented players</p>
            <button 
              className={styles.loginButton}
              onClick={() => navigate('/login')}
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Discover Talent</h1>
          <p className={styles.subtitle}>
            Find and connect with talented players for your team
          </p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className={styles.searchForm}>
          <div className={styles.searchBar}>
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search by player name, sport, position, location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoComplete="off"
            />
            <button type="submit" className={styles.searchButton}>
              Search
            </button>
          </div>
          <button
            type="button"
            className={`${styles.filterButton} ${showFilters ? styles.active : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
            {activeFilterCount > 0 && (
              <span className={styles.filterBadge}>
                {activeFilterCount}
              </span>
            )}
          </button>
        </form>

      </header>

      {/* Filters Modal */}
      {showFilters && (
        <SearchFilters
          filters={filters}
          onFilterChange={(newFilters) => {
            setHasSearched(true);
            updateFilters(newFilters);
          }}
          onClearFilters={() => {
            setHasSearched(false);
            clearFilters();
          }}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Results Section */}
      <div className={styles.results}>
        {/* Results Header */}
        {hasSearched && (
          <div className={styles.resultsHeader}>
            <div className={styles.resultCount}>
              {loading ? (
                <span>Searching...</span>
              ) : (
                <span>
                  {pagination.total} player{pagination.total !== 1 ? 's' : ''} found
                </span>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && profiles.length === 0 && (
          <div className={styles.loadingContainer}>
            <LoadingSpinner size="large" />
            <p>Searching for players...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className={styles.errorContainer}>
            <p>Error loading profiles: {error}</p>
            <button 
              className={styles.retryButton}
              onClick={() => performSearch()}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Results Grid - Only show after search */}
        {!loading && hasSearched && profiles.length > 0 && (
          <div className={styles.profileGrid}>
            {profiles.map(profile => (
              <PlayerCard
                key={profile._id}
                profile={profile}
                isInWatchlist={watchlist.has(profile._id)}
                onToggleWatchlist={canUseWatchlist ? ((e) => toggleWatchlist(profile._id, e)) : null}
                onClick={(e) => viewProfile(profile, e)}
                showWatchlistButton={canUseWatchlist}
              />
            ))}
          </div>
        )}

        {/* Empty State - No Search Yet */}
        {!loading && !hasSearched && (
          <div className={styles.emptyState}>
            <h3>Start Your Search</h3>
            <p>Use the search bar above or try one of the quick searches to find talented players</p>
          </div>
        )}

        {/* Empty State - No Results */}
        {!loading && hasSearched && profiles.length === 0 && !error && (
          <div className={styles.emptyState}>
            <h3>No players found</h3>
            <p>Try adjusting your search criteria or filters</p>
            <button
              className={styles.clearFiltersButton}
              onClick={() => {
                clearFilters();
                setSearchQuery('');
                setHasSearched(false);
              }}
            >
              Clear All Filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageButton}
              onClick={() => changePage(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              Previous
            </button>

            <div className={styles.pageInfo}>
              Page {pagination.page} of {pagination.pages}
            </div>

            <button
              className={styles.pageButton}
              onClick={() => changePage(pagination.page + 1)}
              disabled={pagination.page === pagination.pages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Watchlist Modal */}
      <WatchlistModal
        isOpen={showWatchlistModal}
        onClose={() => {
          setShowWatchlistModal(false);
          setSelectedPlayer(null);
        }}
        player={selectedPlayer}
        onSubmit={handleAddToWatchlist}
      />
    </div>
  );
};

export default TalentDiscovery;
